import { formatCep } from '@services/profile/viaCep.service';
import { onlyDigits } from '@utils/cpf';

import type { Profile, ProfileUpdate } from '@services/profile/profile.types';

export const CEP_REGEX = /^\d{5}-?\d{3}$/;
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface State {
  sigla: string;
  nome: string;
}

export const STATES: State[] = [
  { sigla: 'AC', nome: 'Acre' },
  { sigla: 'AL', nome: 'Alagoas' },
  { sigla: 'AP', nome: 'Amapá' },
  { sigla: 'AM', nome: 'Amazonas' },
  { sigla: 'BA', nome: 'Bahia' },
  { sigla: 'CE', nome: 'Ceará' },
  { sigla: 'DF', nome: 'Distrito Federal' },
  { sigla: 'ES', nome: 'Espírito Santo' },
  { sigla: 'GO', nome: 'Goiás' },
  { sigla: 'MA', nome: 'Maranhão' },
  { sigla: 'MT', nome: 'Mato Grosso' },
  { sigla: 'MS', nome: 'Mato Grosso do Sul' },
  { sigla: 'MG', nome: 'Minas Gerais' },
  { sigla: 'PA', nome: 'Pará' },
  { sigla: 'PB', nome: 'Paraíba' },
  { sigla: 'PR', nome: 'Paraná' },
  { sigla: 'PE', nome: 'Pernambuco' },
  { sigla: 'PI', nome: 'Piauí' },
  { sigla: 'RJ', nome: 'Rio de Janeiro' },
  { sigla: 'RN', nome: 'Rio Grande do Norte' },
  { sigla: 'RS', nome: 'Rio Grande do Sul' },
  { sigla: 'RO', nome: 'Rondônia' },
  { sigla: 'RR', nome: 'Roraima' },
  { sigla: 'SC', nome: 'Santa Catarina' },
  { sigla: 'SP', nome: 'São Paulo' },
  { sigla: 'SE', nome: 'Sergipe' },
  { sigla: 'TO', nome: 'Tocantins' },
];

export interface AddressFormValues {
  cep: string;
  state: string;
  city: string;
  district: string;
  address: string;
  number: string;
  complement: string;
  referencePoint: string;
}

export interface ProfileFormValues {
  name: string;
  social_name: string;
  email: string;
  phone: string;
  birthdate: string;
  mother_name: string;
  addressData: AddressFormValues;
}

const EMPTY_ADDRESS: AddressFormValues = {
  cep: '',
  state: '',
  city: '',
  district: '',
  address: '',
  number: '',
  complement: '',
  referencePoint: '',
};

export function buildDefaultValues(profile: Profile): ProfileFormValues {
  return {
    name: profile.name,
    social_name: profile.nomeSocial ?? '',
    email: profile.email ?? '',
    phone: profile.telefone ?? '',
    birthdate: profile.dataNascimento ?? '',
    mother_name: profile.nomeMae ?? '',
    addressData: profile.endereco
      ? {
          cep: formatCep(profile.endereco.cep),
          state: profile.endereco.estado,
          city: profile.endereco.cidade,
          district: profile.endereco.bairro,
          address: profile.endereco.rua,
          number: profile.endereco.numero,
          complement: profile.endereco.complemento ?? '',
          referencePoint: profile.endereco.pontoReferencia ?? '',
        }
      : { ...EMPTY_ADDRESS },
  };
}

export function hasAnyContent(section?: object): boolean {
  if (!section) return false;
  return Object.values(section).some((v) => typeof v === 'string' && v.trim() !== '');
}

export function toProfileUpdate(values: ProfileFormValues): ProfileUpdate {
  const update: ProfileUpdate = {};

  const personalKeys: { form: keyof ProfileFormValues; api: keyof ProfileUpdate }[] = [
    { form: 'name', api: 'name' },
    { form: 'social_name', api: 'nomeSocial' },
    { form: 'email', api: 'email' },
    { form: 'phone', api: 'telefone' },
    { form: 'birthdate', api: 'dataNascimento' },
    { form: 'mother_name', api: 'nomeMae' },
  ];

  personalKeys.forEach(({ form, api }) => {
    const value = values[form];
    if (typeof value === 'string' && value !== '') {
      (update[api] as string) = value;
    }
  });

  if (hasAnyContent(values.addressData)) {
    const a = values.addressData;
    update.endereco = {
      cep: onlyDigits(a.cep),
      estado: a.state.toUpperCase(),
      cidade: a.city,
      bairro: a.district,
      rua: a.address,
      numero: a.number,
      ...(a.complement ? { complemento: a.complement } : {}),
      ...(a.referencePoint ? { pontoReferencia: a.referencePoint } : {}),
    };
  }

  return update;
}
